/**
 * Shared ecommerce event layer — single-owner architecture.
 *
 * GTM (GTM-MHFPF2WP) is the ONLY browser runtime owner for GA4 and the Meta
 * Pixel. This module therefore does exactly one thing: push a canonical,
 * GA4-shaped ecommerce event to `window.dataLayer` — once per real user
 * action. It never calls `gtag('event', ...)`, never calls `fbq`, and never
 * pushes Meta-named events (ViewContent / AddToCart / InitiateCheckout /
 * Purchase). GTM maps the canonical GA4 names to Meta events.
 *
 * Rules encoded here:
 *   - Currency is INR. All values are RUPEES (never paise).
 *   - Every event carries a stable `event_id` (mirrored as `metaEventId`) so
 *     the GTM browser Meta tag and the server CAPI tag deduplicate on the
 *     same ID.
 *   - Purchase derives its event_id from the authoritative server-issued
 *     verified-order id (`orders.meta_event_id`) and is refused when that id
 *     is missing (i.e. payment not verified).
 *   - Dedup is per event kind + stable key inside a 3s window and — for
 *     purchase — persistently via sessionStorage keyed by transaction_id.
 *   - Ordinary events NEVER carry PII. Consent is enforced by Consent Mode v2
 *     in GTM; this module never bypasses it.
 */

import { recordCommerceEvidence } from "@/lib/conversions/capture";
import { allowAdvertising, allowAnalytics } from "@/lib/consent/enforcement";

export interface CanonicalItem {
  /** Internal SKU or product handle. Used as GA4 `item_id`. */
  id: string;
  /** Product title. */
  name: string;
  /** Optional variant title, e.g. "60-day pack". */
  variant?: string;
  /** Rupees, unit price. */
  price: number;
  quantity: number;
}

const BRAND = "TruWe";
const CURRENCY = "INR";

const seen = new Map<string, number>();
const DEDUP_TTL_MS = 3000;

function dedupCheck(key: string): boolean {
  const now = Date.now();
  const last = seen.get(key);
  if (last && now - last < DEDUP_TTL_MS) return true;
  seen.set(key, now);
  if (seen.size > 200) {
    for (const [k, v] of seen) if (now - v > DEDUP_TTL_MS * 4) seen.delete(k);
  }
  return false;
}

/** Fresh, stable-per-action id shared by GTM's browser Meta + server CAPI tags. */
function newEventId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return (crypto as Crypto).randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function pushDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  // Optional measurement + ad events. With consent enforcement on, nothing is
  // queued for the tag container until the visitor allows a category.
  if (!allowAnalytics() && !allowAdvertising()) return;
  // Record permitted first-party analytics independently of the mixed container.
  recordCommerceEvidence(payload);
  if (!allowAnalytics() || !allowAdvertising()) return;
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  // GA4 recommends clearing the previous ecommerce object before pushing.
  w.dataLayer.push({ ecommerce: null });
  w.dataLayer.push(payload);

}

function toGaItems(items: CanonicalItem[]) {
  return items.map((it, idx) => ({
    item_id: it.id,
    item_name: it.name,
    item_brand: BRAND,
    ...(it.variant ? { item_variant: it.variant } : {}),
    price: Number(it.price.toFixed(2)),
    quantity: it.quantity,
    index: idx,
  }));
}

function sumValue(items: CanonicalItem[]): number {
  return Number(
    items.reduce((s, it) => s + it.price * it.quantity, 0).toFixed(2),
  );
}

// ----- view_item ----------------------------------------------------------

export function trackViewItem(item: CanonicalItem): void {
  if (dedupCheck(`vi:${item.id}`)) return;
  const eventId = newEventId();
  pushDataLayer({
    event: "view_item",
    event_id: eventId,
    metaEventId: eventId,
    ecommerce: {
      currency: CURRENCY,
      value: Number((item.price * item.quantity).toFixed(2)),
      items: toGaItems([item]),
    },
  });
}

// ----- add_to_cart --------------------------------------------------------

export function trackAddToCart(items: CanonicalItem[]): void {
  if (!items.length) return;
  const value = sumValue(items);
  const key = `atc:${items.map((i) => `${i.id}x${i.quantity}`).join("|")}:${value}`;
  if (dedupCheck(key)) return;
  const eventId = newEventId();
  pushDataLayer({
    event: "add_to_cart",
    event_id: eventId,
    metaEventId: eventId,
    ecommerce: {
      currency: CURRENCY,
      value,
      items: toGaItems(items),
    },
  });
}

// ----- begin_checkout -----------------------------------------------------

export interface BeginCheckoutOpts {
  items: CanonicalItem[];
  coupon?: string;
  /** Stable id for this checkout attempt (e.g. cart id + time bucket). */
  attemptKey: string;
}

export function trackBeginCheckout(opts: BeginCheckoutOpts): void {
  if (!opts.items.length) return;
  if (dedupCheck(`bc:${opts.attemptKey}`)) return;
  const value = sumValue(opts.items);
  const eventId = newEventId();
  pushDataLayer({
    event: "begin_checkout",
    event_id: eventId,
    metaEventId: eventId,
    ecommerce: {
      currency: CURRENCY,
      value,
      ...(opts.coupon ? { coupon: opts.coupon } : {}),
      items: toGaItems(opts.items),
    },
  });
}

// ----- purchase -----------------------------------------------------------

export interface PurchaseOpts {
  /** Stable transaction id (order name). Used as GA4 transaction_id. */
  transactionId: string;
  /** Server-issued dedup id (orders.meta_event_id), only issued post-verify. */
  metaEventId: string;
  /** Rupees. */
  value: number;
  /** Rupees; omit if unavailable. */
  tax?: number;
  /** Rupees; omit if unavailable. */
  shipping?: number;
  coupon?: string;
  items: CanonicalItem[];
}

/**
 * Persistent per-transaction guard. The server's payment-verification flow
 * is the authoritative "purchase happened" gate (metaEventId is only issued
 * post-verification). This sessionStorage guard defeats refreshes / back-
 * forward navigation for GA4 dedup — GA4 further dedups on transaction_id.
 */
function purchaseAlreadyFired(transactionId: string): boolean {
  if (typeof window === "undefined") return true;
  try {
    const k = `truwe_ga_purchase:${transactionId}`;
    if (window.sessionStorage.getItem(k)) return true;
    window.sessionStorage.setItem(k, "1");
    return false;
  } catch {
    return false;
  }
}

export function trackPurchase(opts: PurchaseOpts): boolean {
  if (!opts.transactionId || !opts.metaEventId) return false;
  if (!opts.items.length) return false;
  if (purchaseAlreadyFired(opts.transactionId)) return false;
  const value = Number(opts.value.toFixed(2));
  pushDataLayer({
    event: "purchase",
    // Authoritative, server-issued id: identical for GTM browser Meta and
    // server CAPI so Meta deduplicates the two deliveries.
    event_id: opts.metaEventId,
    metaEventId: opts.metaEventId,
    ecommerce: {
      transaction_id: opts.transactionId,
      currency: CURRENCY,
      value,
      ...(typeof opts.tax === "number" ? { tax: Number(opts.tax.toFixed(2)) } : {}),
      ...(typeof opts.shipping === "number"
        ? { shipping: Number(opts.shipping.toFixed(2)) }
        : {}),
      ...(opts.coupon ? { coupon: opts.coupon } : {}),
      items: toGaItems(opts.items),
    },
  });
  return true;
}

// ----- generate_lead ------------------------------------------------------

export interface GenerateLeadOpts {
  /** Recommended product handle/SKU from the quiz. */
  recommendedSku: string;
  /** Quiz primary goal id. */
  quizPrimaryGoal: string;
  /** Rupees — price of the recommended pack. */
  value: number;
  /**
   * Stable dedup id shared by the GTM browser Meta tag and the server CAPI
   * tag. Generate it at the call site so the same id can be persisted with
   * the lead record.
   */
  eventId: string;
}

/**
 * Push the canonical GA4 `generate_lead` event once. GTM maps it to Meta
 * `Lead` (browser Pixel + server CAPI) and deduplicates on `event_id`.
 *
 * Marketing-consent gated: the lead record itself is always saved by the
 * server function, but this ad-tracking event only fires when the visitor
 * granted marketing consent.
 */
export function trackGenerateLead(_opts: GenerateLeadOpts): boolean {
  // Quiz completion and recommendations are derived from health answers.
  // Advertising consent does not authorise restricted sensitive-data sharing.
  return false;
}

/** Fresh dedup id, exported so callers can persist it with the lead. */
export function createEventId(): string {
  return newEventId();
}
